// -- == //
import { Column, Line } from '@ant-design/plots';

import * as S from './styles';
import Empty from 'components/empty';
import PanelBoxLayout from 'components/layout/panelBox';
import { seriesColor, seriesDash, seriesSlots } from 'common/helpers/chartColors';
// import * as T from "common/types";

interface IProps {
  data: any[];
  totalOrders?: any[];
  /** `July 2025`, straight off the chart payload. Names the range in the head. */
  month?: string;
  /**
   * Every menu item that exists, in the order the menu list gives them — not
   * just the ones the filter currently has selected. It is what pins a colour
   * to an item: deselect one and the rest keep the colours they had.
   */
  items?: string[];
  /** Set when the chart request failed. Changes what the empty state says. */
  error?: any;
  // loading?: boolean;
}

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round">
    <path d="M3 20h18M6 16l4-5 4 3 5-8" />
  </svg>
);

export default function CafeteriaStatistics({ data, month, items = [], error }: IProps) {
  function convertData() {
    const convertedData: any = [];
    data?.forEach((dayData) => {
      Object.keys(dayData).forEach((key) => {
        if (key !== 'day') {
          const item = key;
          const day = dayData.day.toString();
          const value = dayData[key];
          const existingRecordIndex = convertedData.findIndex(
            (record: any) => record.item === item && record.day === day
          );

          if (existingRecordIndex !== -1) {
            convertedData[existingRecordIndex].value += value;
          } else {
            convertedData.push({
              item,
              day,
              value,
              index: convertedData.length + 1,
              datum_index: convertedData.length + 1
            });
          }
        }
      });
    });

    return convertedData.sort((a: any, b: any) => a.day - b.day);
  }

  const rows = convertData();

  /*
   * The endpoint always returns the whole month — one row per calendar day,
   * with a zero against every selected item — so on the 16th of September it
   * sends days 1 to 30 and the chart drew a flat line across two weeks that
   * have not happened yet. Worse, the line fell back to zero on the 17th,
   * which reads as "no orders tomorrow" rather than "tomorrow is not here".
   *
   * So the range stops at today, and only for the month that contains today:
   * a past month is complete and a future one is empty, and neither needs
   * trimming. The month is matched against the payload's own label rather
   * than parsed, because that label was built by this same formatter on the
   * server — `September 2026` — and comparing two strings made the same way
   * cannot disagree about which month it is.
   */
  const now = new Date();
  const thisMonth = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(now);
  const monthInProgress = !!month && month === thisMonth;
  const elapsed = monthInProgress ? rows.filter((record: any) => Number(record.day) <= now.getDate()) : rows;

  /*
   * An item nobody ordered is a flat line on zero and a name in the legend,
   * and the filter selects every item on the menu by default: thirty series
   * to carry the three or four that have any orders in them, two rows of
   * legend under the chart, and thirty entries in every tooltip.
   *
   * Only the items with orders are drawn. The head says how many of the
   * selected items that was, so a missing line is stated rather than silently
   * dropped — which is the difference between a chart that is summarising and
   * one that is hiding something.
   */
  const totals = elapsed.reduce((sums: Record<string, number>, record: any) => {
    sums[record.item] = (sums[record.item] || 0) + record.value;
    return sums;
  }, {});

  const selected = Object.keys(totals).length;
  const newData = elapsed.filter((record: any) => totals[record.item] > 0);
  const ordered = new Set(newData.map((record: any) => record.item)).size;

  /** A month with no orders at all is an empty range, not an empty payload. */
  const hasOrders = newData.length > 0;

  /*
   * What the range actually covers, beside the title: the month, whether it is
   * still running, and how much of the item selection is on the chart.
   */
  const hint = [
    month,
    monthInProgress ? 'to date' : null,
    hasOrders && ordered < selected ? `${ordered} of ${selected} items ordered` : null
  ]
    .filter(Boolean)
    .join(' · ');

  /*
   * Two different nothings, and the screen must not confuse them.
   *
   * A failed request also produces an empty `data`, and calling that "no
   * orders in this range" would state something about the business that the
   * panel does not know. The endpoint returns 200 now that the serialiser is
   * fixed, so the second branch is the usual one — but the distinction is
   * what keeps the screen honest the next time it is not.
   */
  const emptyState = error ? (
    <Empty
      size="sm"
      icon={<ChartIcon />}
      title="Orders could not be loaded"
      text="The chart request was rejected by the server. Nothing here reflects real order volume."
    />
  ) : (
    <Empty
      size="sm"
      icon={<ChartIcon />}
      title="No orders in this range"
      text="Pick a different month, or widen the item selection in the filter."
    />
  );

  /*
   * One colour per menu item.
   *
   * Both charts split into one series per item — and both painted every one of
   * them the same hue, because `seriesField` alone is the v1 spelling: under
   * plots v2 (G2 v5) it only splits the data, and `colorField` is what paints
   * it. A twenty-name legend against twenty identical swatches names the items
   * without letting you find any of them on the chart.
   *
   * The scale is pinned by domain so a hue belongs to an item rather than to
   * its rank in whatever the filter last returned — see `chartColors` for the
   * palette and why its order is what it is.
   */
  const series: string[] = [];
  newData.forEach((record: any) => {
    if (!series.includes(record.item)) series.push(record.item);
  });

  const slots = seriesSlots(series, items);

  const colorScale = {
    color: {
      domain: series,
      range: series.map((name) => seriesColor(slots[name]))
    }
  };

  /** Legend, tooltip and ordering are the same on both charts; the mark is not. */
  const legend = {
    color: {
      position: 'bottom' as const,
      layout: { justifyContent: 'center' }
    }
  };

  /** Orders per day across the drawn items — what the tooltip's head needs. */
  const perDay = newData.reduce((sums: Record<string, number>, record: any) => {
    sums[record.day] = (sums[record.day] || 0) + record.value;
    return sums;
  }, {});

  /*
   * The day, and — when there were no orders at all — that fact.
   *
   * A titled tooltip is never treated as empty, so on a quiet day the zero
   * filter below leaves a box with a heading and nothing under it, which
   * reads as a tooltip that failed to load. The head answers for itself
   * instead; it is read off our own data rather than the filtered rows,
   * which is the only place the distinction still exists by then.
   */
  const tooltip = {
    title: (record: any) => `Day ${record.day}${perDay[record.day] ? '' : ' · no orders'}`,
    /*
     * Name each row by its item. Hovering a single bar otherwise labels it
     * from the measure — a tooltip reading "value 2" against a chart whose
     * whole point is which item it was.
     */
    items: [(record: any) => ({ name: record.item, value: record.value })]
  };

  /*
   * The tooltip is shared across the series, so it lists every item on the
   * day under the cursor — and an item with no orders that day still had a
   * row, at zero. On a thirty-item selection that was a dialog taller than
   * the chart, in which the two lines that meant something sat somewhere in
   * the middle of twenty-eight zeroes.
   *
   * Zeroes are dropped and what is left runs busiest-first, so the row a
   * reader wants is the first one.
   */
  const interaction = {
    tooltip: {
      sort: (item: any) => -Number(item.value),
      filter: (item: any) => Number(item.value) > 0
    }
  };

  const configColumn = {
    data: newData,
    xField: 'day',
    yField: 'value',
    seriesField: 'item',
    colorField: 'item',
    scale: colorScale,
    legend,
    tooltip,
    interaction
  };

  const configLine = {
    data: newData,
    xField: 'day',
    yField: 'value',
    seriesField: 'item',
    colorField: 'item',
    scale: colorScale,
    style: {
      lineWidth: 2,
      /*
       * Past the twelfth item the palette starts a second lap, so the repeat
       * carries a dash pattern to stay distinct from the hue's first use. G2
       * hands the callback the records behind one line; reading either a row
       * or an array of rows covers it, and anything else falls through to
       * undefined, which is a solid line.
       */
      lineDash: (datum: any) => {
        const record = Array.isArray(datum) ? datum[0] : datum;
        return seriesDash(slots[record?.item]);
      }
    },
    legend,
    tooltip,
    interaction
  };

  /*
   * The two headings named the chart type — "Order Amounts Line" and "Order
   * Amounts Column" — which tells the reader what they can already see and
   * not what it is of. They name the data now. The hint carries the month the
   * payload says it is showing, which nothing on the screen used to state.
   */
  return (
    <S.Content>
      <PanelBoxLayout title="Orders over time" hint={hint}>
        {hasOrders ? <Line {...configLine} /> : emptyState}
      </PanelBoxLayout>
      <PanelBoxLayout title="Orders by day and item" hint={hint}>
        {hasOrders ? <Column {...configColumn} /> : emptyState}
      </PanelBoxLayout>
    </S.Content>
  );
}
